import { create } from "zustand";

const COLLAPSE_KEY = "sidebarCollapsed";
const WIDTH_KEY = "sidebarWidth";

export const SIDEBAR_MIN_WIDTH = 200;
export const SIDEBAR_MAX_WIDTH = 480;
export const SIDEBAR_DEFAULT_WIDTH = 240;

interface SidebarStore {
  mobileOpen: boolean;
  desktopCollapsed: boolean;
  width: number;
  open: () => void;
  close: () => void;
  toggle: () => void;
  toggleCollapsed: () => void;
  setWidth: (w: number) => void;
}

export function clampSidebarWidth(w: number): number {
  if (!Number.isFinite(w)) return SIDEBAR_DEFAULT_WIDTH;
  return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, Math.round(w)));
}

export function applySidebarWidthVar(w: number): void {
  if (typeof document !== "undefined") {
    document.documentElement.style.setProperty("--sidebar-w", `${w}px`);
  }
}

function initialCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(COLLAPSE_KEY) === "1";
}

function initialWidth(): number {
  if (typeof window === "undefined") return SIDEBAR_DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(WIDTH_KEY);
  const width = raw === null ? SIDEBAR_DEFAULT_WIDTH : clampSidebarWidth(parseInt(raw, 10));
  applySidebarWidthVar(width);
  return width;
}

export const useSidebar = create<SidebarStore>((set) => ({
  mobileOpen: false,
  desktopCollapsed: initialCollapsed(),
  width: initialWidth(),
  open: () => set({ mobileOpen: true }),
  close: () => set({ mobileOpen: false }),
  toggle: () => set((s) => ({ mobileOpen: !s.mobileOpen })),
  toggleCollapsed: () =>
    set((s) => {
      const next = !s.desktopCollapsed;
      if (typeof window !== "undefined") {
        window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      }
      return { desktopCollapsed: next };
    }),
  setWidth: (w) =>
    set(() => {
      const next = clampSidebarWidth(w);
      applySidebarWidthVar(next);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(WIDTH_KEY, String(next));
      }
      return { width: next };
    }),
}));
